import { makeSignedPercent } from '@/helpers/chartFormat'

export const roundEstimate = (value: number | null | undefined): number | null => (value == null ? null : Math.round(value))

export const deltaToRatio = (value: number | null | undefined, delta: number | null | undefined): number | null => {
  if (value == null || delta == null) return null
  const base = value - delta
  if (base === 0) return null
  return delta / base
}

export const sumInWindow = <Row extends { period?: string | null }>(
  rows: Row[],
  pick: (row: Row) => number | null | undefined,
  from: string | null,
  to: string | null,
  { missingAsZero = false }: { missingAsZero?: boolean } = {},
): number | null => {
  if (!from || !to) return null
  const values = rows.filter((row) => row.period && row.period >= from && row.period <= to).map(pick)
  if (!missingAsZero && (!values.length || values.some((value) => value == null))) return null
  return values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
}

export type TrendDirection = 'up' | 'down' | 'flat'

export const direction = (value: number): TrendDirection => (value > 0 ? 'up' : value < 0 ? 'down' : 'flat')

export interface PercentTrend {
  value: string
  direction: TrendDirection
}

const roundToDisplayedPercent = (ratio: number): number => Math.round(ratio * 1000) / 1000

export const percentTrend = (ratio: number | null, locale: string): PercentTrend | undefined => {
  if (ratio == null) return undefined
  return { value: makeSignedPercent(locale)(ratio), direction: direction(roundToDisplayedPercent(ratio)) }
}

export const inWindow = <Row extends { period?: string | null }>(rows: Row[], from: string | null, to: string | null): Row[] =>
  from && to ? rows.filter((row) => row.period && row.period >= from && row.period <= to) : []

export const previousWindowRatio = <Row extends { period?: string | null }>(
  rows: Row[],
  pick: (row: Row) => number | null | undefined,
  { previousStart, previousEnd, months }: { previousStart: string | null; previousEnd: string | null; months: number },
  current: number | null,
): number | null => {
  const previousRows = inWindow(rows, previousStart, previousEnd)
  if (current == null || previousRows.length !== months) return null
  const previous = sumInWindow(previousRows, pick, previousStart, previousEnd)
  return previous == null ? null : deltaToRatio(current, current - previous)
}

export const isoWeekLabel = (period: string | null | undefined): string => {
  if (!period) return ''
  const date = new Date(period)
  if (Number.isNaN(date.getTime())) return ''
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  const dayNr = (target.getUTCDay() + 6) % 7
  target.setUTCDate(target.getUTCDate() - dayNr + 3)
  const firstThursday = target.getTime()
  target.setUTCMonth(0, 1)
  if (target.getUTCDay() !== 4) {
    target.setUTCMonth(0, 1 + ((4 - target.getUTCDay() + 7) % 7))
  }
  return `W${1 + Math.ceil((firstThursday - target.getTime()) / 604800000)}`
}

const monthFormatterCache = new Map<string, Intl.DateTimeFormat>()

const monthFormatter = (locale: string, month: 'short' | 'long'): Intl.DateTimeFormat => {
  const key = `${locale}:${month}`
  const cached = monthFormatterCache.get(key)
  if (cached) return cached
  const formatter = new Intl.DateTimeFormat(locale, { month, timeZone: 'UTC' })
  monthFormatterCache.set(key, formatter)
  return formatter
}

export const monthLabel = (period: string | null | undefined, locale: string, month: 'short' | 'long' = 'short'): string => {
  if (!period) return ''
  const date = new Date(period)
  if (Number.isNaN(date.getTime())) return ''
  return monthFormatter(locale, month).format(date)
}
